/**
 * Notepad — reads plain-text files from three sources:
 *   1. Static file (blog.txt, about-me.txt) via the `src` prop.
 *   2. Server-persisted note via the `noteId` prop → GET /api/notes/:id.
 *   3. Freshly-composed text with no backing store.
 *
 * When a user is signed in:
 *   - File → Save            (Ctrl+S) — PUT or POST depending on state.
 *   - File → Save to My Notes         — POST a copy as a new note.
 *
 * The read-only `src`-driven mode (blog.txt, about-me.txt) leaves those
 * menu items disabled so the classic public-content viewer isn't confused
 * with the persistent notes UI.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { WindowDropDowns } from 'components';
import { useAuth } from 'auth/AuthContext';
import { api, ApiError } from 'api/client';

import {
  buildFileMenu,
  editMenu,
  formatMenu,
  viewMenu,
  helpMenu,
} from './dropDownData';

export default function Notepad({
  onClose,
  defaultText = '',
  src,
  noteId: initialNoteId,
  title: initialTitle,
}) {
  const { user } = useAuth();

  const [docText, setDocText] = useState(defaultText);
  const [title, setTitle] = useState(initialTitle || '');
  const [noteId, setNoteId] = useState(initialNoteId || null);
  const [dirty, setDirty] = useState(false);
  const [wordWrap, setWordWrap] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const textAreaRef = useRef(null);
  // We freeze the last-server-sync'd text so a fresh fetch response doesn't
  // spuriously flip the dirty flag.
  const lastLoadedText = useRef(defaultText);

  const canSave = !!user && !src;

  // ---- Load from server when a noteId is provided at mount ----------------
  useEffect(() => {
    if (!initialNoteId) return undefined;
    let cancelled = false;
    setBusy(true);
    api
      .get('/api/notes/' + initialNoteId)
      .then(data => {
        if (cancelled || !data || !data.note) return;
        lastLoadedText.current = data.note.body || '';
        setDocText(data.note.body || '');
        setTitle(data.note.title || '');
        setNoteId(data.note.id);
        setDirty(false);
      })
      .catch(err => {
        if (cancelled) return;
        setStatusMessage(loadErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialNoteId]);

  // ---- Load from static file (blog.txt / about-me.txt) --------------------
  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;

    function loadText() {
      const separator = src.indexOf('?') === -1 ? '?' : '&';
      const freshSrc = `${src}${separator}_=${Date.now()}`;
      fetch(freshSrc, { cache: 'no-store' })
        .then(r => (r.ok ? r.text() : ''))
        .then(text => {
          if (cancelled) return;
          lastLoadedText.current = text;
          setDocText(text);
        })
        .catch(() => {});
    }

    function onContentRefresh(event) {
      if (event.detail === 'BLOG.txt' && /blog\.txt(?:$|\?)/i.test(src)) {
        loadText();
      }
    }

    loadText();
    window.addEventListener('lottel:content-refresh', onContentRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('lottel:content-refresh', onContentRefresh);
    };
  }, [src]);

  function onChangeText(e) {
    const next = e.target.value;
    setDocText(next);
    if (next !== lastLoadedText.current) setDirty(true);
  }

  // ---- Save actions -------------------------------------------------------
  const saveExisting = useCallback(async () => {
    if (!noteId || busy) return;
    setBusy(true);
    setStatusMessage('');
    try {
      const data = await api.put('/api/notes/' + noteId, {
        title: title || 'Untitled',
        body: docText,
      });
      if (data && data.note) {
        lastLoadedText.current = data.note.body || '';
        setTitle(data.note.title || '');
        setDirty(false);
        setStatusMessage('Saved.');
        window.dispatchEvent(new CustomEvent('lottel:notes-changed'));
      }
    } catch (err) {
      setStatusMessage(saveErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [noteId, docText, title, busy]);

  const saveAsNew = useCallback(async () => {
    if (busy) return;
    // Guess a title from the first non-empty line, or the current title.
    const firstLine =
      docText.split(/\r?\n/).find(line => line.trim().length > 0) || '';
    const guess = title || firstLine.slice(0, 60) || 'Untitled';
    // eslint-disable-next-line no-alert
    const chosen = window.prompt('Save to My Notes — title:', guess);
    if (chosen === null) return;
    const trimmed = chosen.trim() || 'Untitled';
    setBusy(true);
    setStatusMessage('');
    try {
      const data = await api.post('/api/notes', {
        title: trimmed,
        body: docText,
      });
      if (data && data.note) {
        lastLoadedText.current = data.note.body || '';
        setTitle(data.note.title || '');
        setNoteId(data.note.id);
        setDirty(false);
        setStatusMessage('Saved to My Notes.');
        window.dispatchEvent(new CustomEvent('lottel:notes-changed'));
      }
    } catch (err) {
      setStatusMessage(saveErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [docText, title, busy]);

  const trySave = useCallback(() => {
    if (!canSave) return;
    if (noteId) {
      saveExisting();
    } else {
      saveAsNew();
    }
  }, [canSave, noteId, saveExisting, saveAsNew]);

  // ---- Ctrl+S -------------------------------------------------------------
  useEffect(() => {
    if (!canSave) return undefined;
    const el = textAreaRef.current;
    if (!el) return undefined;
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        trySave();
      }
    }
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [canSave, trySave]);

  // ---- Dropdown data ------------------------------------------------------
  const dropDownData = useMemo(
    () => ({
      File: buildFileMenu({
        canSave,
        hasNoteId: !!noteId,
      }),
      Edit: editMenu,
      Format: formatMenu,
      View: viewMenu,
      Help: helpMenu,
    }),
    [canSave, noteId],
  );

  function onClickOptionItem(item) {
    switch (item) {
      case 'Exit':
        onClose();
        break;
      case 'Word Wrap':
        setWordWrap(!wordWrap);
        break;
      case 'Save':
        trySave();
        break;
      case 'Save to My Notes':
        saveAsNew();
        break;
      case 'Time/Date': {
        const date = new Date();
        const stamp = `${date.toLocaleTimeString()} ${date.toLocaleDateString()}`;
        setDocText(prev => {
          const next = `${prev}${stamp}`;
          if (next !== lastLoadedText.current) setDirty(true);
          return next;
        });
        break;
      }
      default:
    }
  }

  function onTextAreaKeyDown(e) {
    // handle tabs in text area
    if (e.which === 9) {
      e.preventDefault();
      e.persist();
      var start = e.target.selectionStart;
      var end = e.target.selectionEnd;
      setDocText(prev => {
        const next = `${prev.substring(0, start)}\t${prev.substring(end)}`;
        if (next !== lastLoadedText.current) setDirty(true);
        return next;
      });

      // asynchronously update textarea selection to include tab
      // workaround due to https://github.com/facebook/react/issues/14174
      requestAnimationFrame(() => {
        e.target.selectionStart = start + 1;
        e.target.selectionEnd = start + 1;
      });
    }
  }

  const showStatus =
    statusMessage || (noteId && dirty ? 'Unsaved changes' : '');
  const showTitleSuffix = title && noteId ? ` — ${title}` : '';

  return (
    <Div>
      <section className="np__toolbar">
        <WindowDropDowns items={dropDownData} onClickItem={onClickOptionItem} />
      </section>
      <StyledTextarea
        ref={textAreaRef}
        wordWrap={wordWrap}
        value={docText}
        onChange={onChangeText}
        onKeyDown={onTextAreaKeyDown}
        spellCheck={false}
      />
      {(showStatus || busy) && (
        <div className="np__status">
          {busy ? 'Saving…' : showStatus}
          {showTitleSuffix}
        </div>
      )}
    </Div>
  );
}

// ---------- Error formatting -----------------------------------------------

function saveErrorMessage(err) {
  if (err instanceof ApiError) {
    const map = {
      unauthorized: 'You must be signed in to save.',
      csrf_invalid: 'Session expired — please reload the page.',
      invalid_title: 'Title is required (1-120 characters).',
      body_too_large: 'This note is too large to save.',
      note_count_exceeded:
        'You have reached the maximum number of saved notes. Delete some in My Notes.',
      note_quota_exceeded:
        'Your total note storage is full. Delete some notes to free space.',
      not_found: 'That note no longer exists.',
      network_error: 'Cannot reach the server.',
    };
    return map[err.code] || err.message || 'Could not save.';
  }
  return 'Could not save.';
}

function loadErrorMessage(err) {
  if (err instanceof ApiError && err.code === 'not_found') {
    return 'That note could not be loaded.';
  }
  return 'Could not load note.';
}

// ---------- Styles ---------------------------------------------------------

const Div = styled.div`
  height: 100%;
  background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  .np__toolbar {
    position: relative;
    height: 21px;
    flex-shrink: 0;
    border-bottom: 1px solid white;
  }
  .np__status {
    flex-shrink: 0;
    padding: 2px 6px;
    background: #ece9d8;
    border-top: 1px solid #a4b0be;
    font-family: Tahoma, sans-serif;
    font-size: 11px;
    color: #333;
  }
`;

const StyledTextarea = styled.textarea`
  flex: auto;
  outline: none;
  font-family: 'Lucida Console', monospace;
  font-size: 13px;
  line-height: 14px;
  resize: none;
  padding: 2px;
  ${props => (props.wordWrap ? '' : 'white-space: nowrap; overflow-x: scroll;')}
  overflow-y: scroll;
  border: 1px solid #96abff;
`;
